import { inject } from '@angular/core';
import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { from, throwError, timer } from 'rxjs';
import { catchError, retry, switchMap, retryWhen, mergeMap } from 'rxjs/operators';
import { MSAL_INSTANCE } from '@azure/msal-angular';
import { environment } from 'src/app/core/environments/environment';
import { AppInitService } from '../services/shared-service/app-init.service';
import { PublicClientApplication, SilentRequest } from '@azure/msal-browser';
import { AuthService } from 'src/app/core/services/auth.service';

export const enghubapiInterceptor: HttpInterceptorFn = (req, next) => {
  // If not cloud, pass the request without modification
  const authService = inject(AuthService);
  
  if (!environment.name.includes('cloud')) {
    const token = authService.getToken();

    if (!token || (!req.url.startsWith('/') && !req.url.includes(environment.api.url))) {
      return next(req);
    }

    return next(
      req.clone({
        setHeaders: {
          accept: '*/*',
          Authorization: `Bearer ${token}`,
        },
      })
    );
  }

  const msalInstance = inject<PublicClientApplication>(MSAL_INSTANCE);
  const appInitService = inject(AppInitService);

  // Derive origins from the current environment instead of hardcoding a single (dev) domain
  const enghubOrigin = new URL(environment.api.url).origin;
  const engtoolOrigin = environment.engtoolapi ? new URL(environment.engtoolapi).origin : '';

  let request = req;
  const isFormData = req.body instanceof FormData;

  try {
    const urlOrigin = req.url.startsWith('http')
      ? new URL(req.url).origin
      : window.location.origin;

    // EngHub API
    if (urlOrigin.includes(enghubOrigin)) {
      const accessToken = sessionStorage.getItem('access_token') || '';
      const resource = sessionStorage.getItem('resource') || '';
      const slot = sessionStorage.getItem('slot') || '';

      if (req.method !== 'OPTIONS') {
        request = req.clone({
          setHeaders: {
            accept: '*/*',
            Authorization: `Bearer ${accessToken}`,
            'X-Resource-Id': resource,
            'X-Slot-Id': slot,
          },
        });
      }
    }

    // EngTool API
    else if (urlOrigin === engtoolOrigin) {
      const idToken = sessionStorage.getItem('idtoken') || '';

      if (req.method !== 'OPTIONS') {
        request = req.clone({
          setHeaders: {
            Accept: 'application/json',
            Authorization: `Bearer ${idToken}`,
            ...(isFormData ? {} : { 'Content-Type': 'application/json; charset=utf-8' }),
          },
        });
      }
    }
  } catch (e) {
    console.warn('Error parsing URL on the interceptor:', e);
  }

  return next(request).pipe(
    retryWhen(errors =>
      errors.pipe(
        mergeMap((error, index) => {
          // Only retry if headers are missing and we haven't exceeded 3 attempts
          if (index < 3) {
            const hasAuthHeader = request.headers.has('Authorization');
            const accessToken = sessionStorage.getItem('access_token');

            // If authorization header is missing but should be present, retry
            if (!hasAuthHeader && accessToken) {
              console.warn(`Retry attempt ${index + 1}: Headers missing, retrying request...`);

              // Reclone the request with the correct headers
              const urlOrigin = req.url.startsWith('http')
                ? new URL(req.url).origin
                : window.location.origin;

              // EngHub API
              if (urlOrigin.includes(enghubOrigin)) {
                const resource = sessionStorage.getItem('resource') || '';
                const slot = sessionStorage.getItem('slot') || '';

                request = req.clone({
                  setHeaders: {
                    accept: '*/*',
                    Authorization: `Bearer ${accessToken}`,
                    'X-Resource-Id': resource,
                    'X-Slot-Id': slot,
                  },
                });
              }
              // EngTool API
              else if (urlOrigin === engtoolOrigin) {
                const idToken = sessionStorage.getItem('idtoken') || '';

                request = req.clone({
                  setHeaders: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${idToken}`,
                    ...(isFormData ? {} : { 'Content-Type': 'application/json; charset=utf-8' }),
                  },
                });
              }
              // Other APIs
              else {
                const resource = sessionStorage.getItem('resource') || '';
                const slot = sessionStorage.getItem('slot') || '';

                request = req.clone({
                  setHeaders: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                    'X-Resource-Id': resource,
                    'X-Slot-Id': slot,
                    ...(isFormData ? {} : { 'Content-Type': 'application/json; charset=utf-8' }),
                  },
                });
              }

              return timer(500 * (index + 1)); // Incremental delay
            }
          }

          // If not a retry case, propagate the error
          return throwError(() => error);
        })
      )
    ),
    catchError((err: HttpErrorResponse) => {
      if (err.status === 401) {
        const accountStr = sessionStorage.getItem('account');
        if (!accountStr) {
          console.warn('No active account. Cannot renew the token.');
          return throwError(() => err);
        }

        const account = JSON.parse(decodeURIComponent(accountStr));
        const silentRequest: SilentRequest = {
          account,
          scopes: ['user.read'],
        };

        return from(msalInstance.acquireTokenSilent(silentRequest)).pipe(
          switchMap((idTokenResponse: any) => {
            // Save IdToken
            sessionStorage.setItem('idtoken', idTokenResponse.idToken);
            sessionStorage.setItem('username', idTokenResponse.account.username);

            return appInitService.postControlSystemsToken$().pipe(
              switchMap(() => {
                const accessToken = sessionStorage.getItem('access_token');
                const idToken = sessionStorage.getItem('idtoken');

                if (!accessToken && !idToken) {
                  return throwError(() => new Error('No tokens available after renewal.'));
                }

                const retryIsFormData = request.body instanceof FormData;
                const retryUrlOrigin = request.url.startsWith('http')
                  ? new URL(request.url).origin
                  : window.location.origin;

                let retryRequest;

                // EngTool API
                if (retryUrlOrigin === engtoolOrigin) {
                  retryRequest = request.clone({
                    setHeaders: {
                      Accept: 'application/json',
                      Authorization: `Bearer ${idToken}`,
                      ...(retryIsFormData ? {} : { 'Content-Type': 'application/json; charset=utf-8' }),
                    },
                  });
                }
                // EngHub API
                else if (retryUrlOrigin.includes(enghubOrigin)) {
                  retryRequest = request.clone({
                    setHeaders: {
                      accept: '*/*',
                      Authorization: `Bearer ${accessToken}`,
                      'X-Resource-Id': sessionStorage.getItem('resource') || '',
                      'X-Slot-Id': sessionStorage.getItem('slot') || '',
                    },
                  });
                }
                // Default fallback
                else {
                  retryRequest = request.clone({
                    setHeaders: {
                      Accept: 'application/json',
                      Authorization: `Bearer ${accessToken}`,
                      'X-Resource-Id': sessionStorage.getItem('resource') || '',
                      'X-Slot-Id': sessionStorage.getItem('slot') || '',
                      ...(retryIsFormData ? {} : { 'Content-Type': 'application/json; charset=utf-8' }),
                    },
                  });
                }

                return next(retryRequest).pipe(
                  retry(3),
                  catchError(finalErr => throwError(() => finalErr))
                );
              })
            );
          }),
          catchError((tokenErr) => {
            console.error('Error renewing token:', tokenErr);
            return throwError(() => err);
          })
        );
      } else {
        return throwError(() => err);
      }
    })
  );
};
